import acerto from '@acerto/web-sdk';
import { detectarPlataformaPwa } from '@shared/pwa/utils/plataformaPwa';
import logger from '@/logger';
import type { PlataformaPwa } from '@/modules/events/acerto';

/**
 * O `public/sw.js` abre o mesmo banco: nome, versão, store e chave precisam ficar iguais nos dois, o
 * que `__tests__/public/swContratoPagina.spec.ts` confere.
 */
export const BANCO = 'acerto-pwa';
export const VERSAO_BANCO = 1;
export const STORE = 'contexto';
export const CHAVE_CONTEXTO_EVENTOS = 'eventos';

/**
 * Os mesmos hosts que o `@acerto/web-sdk` usa para o EventHub, que ele não expõe. O SDK lê
 * cookies e `document` e não roda no service worker, que publica os eventos de notificação
 * direto no EventHub, com o mesmo contrato.
 */
export const HOST_EVENTHUB = {
  dev: 'https://envdevapp.acerto.com.br',
  hmg: 'https://envhmgapp.acerto.com.br',
  prd: 'https://app.acerto.com.br',
} as const;

export const ROTA_EVENTHUB = '/api/eventhub/api/evento';

export interface ContextoEventosServiceWorker {
  url: string;
  deviceId: string;
  sessionId: string;
  plataforma: PlataformaPwa;
}

function ehAmbienteEventHub(ambiente: string): ambiente is keyof typeof HOST_EVENTHUB {
  return ambiente in HOST_EVENTHUB;
}

/** Sem ambiente configurado, o SDK cai em `dev`; o service worker segue o mesmo destino. */
function urlEventHub(): string {
  const ambiente = appSettings.VUE_APP_AMBIENTE ?? '';
  const host = ehAmbienteEventHub(ambiente) ? HOST_EVENTHUB[ambiente] : HOST_EVENTHUB.dev;
  return `${host}${ROTA_EVENTHUB}`;
}

function abrirBanco(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const requisicao = indexedDB.open(BANCO, VERSAO_BANCO);
    requisicao.onupgradeneeded = () => {
      requisicao.result.createObjectStore(STORE);
    };
    requisicao.onsuccess = () => resolve(requisicao.result);
    requisicao.onerror = () => reject(requisicao.error ?? new Error(`Falha ao abrir o IndexedDB ${BANCO}`));
  });
}

function gravar(banco: IDBDatabase, contexto: ContextoEventosServiceWorker): Promise<void> {
  return new Promise((resolve, reject) => {
    const transacao = banco.transaction(STORE, 'readwrite');
    transacao.objectStore(STORE).put(contexto, CHAVE_CONTEXTO_EVENTOS);
    transacao.oncomplete = () => resolve();
    transacao.onerror = () => reject(transacao.error ?? new Error('Falha ao gravar o contexto de eventos do SW'));
  });
}

/**
 * Grava no IndexedDB o destino e os identificadores com que o service worker publica os eventos
 * de notificação. O push chega com o app fechado, quando só o service worker roda, e ele não lê
 * `localStorage` nem cookies. Chamada no boot, logo depois do `acerto.initialize()`, que define o
 * `deviceId` e o `sessionId`. O `userId` fica de fora de propósito: num dispositivo compartilhado,
 * o último usuário gravado não é necessariamente o dono da notificação, e ele vem no próprio push.
 * Nunca lança.
 */
export async function salvarContextoEventosServiceWorker(): Promise<void> {
  if (!('serviceWorker' in navigator) || !('indexedDB' in window)) return;

  const deviceId = acerto.getDeviceId();
  const sessionId = acerto.getSessionId();
  if (!deviceId || !sessionId) return;

  try {
    const banco = await abrirBanco();
    try {
      await gravar(banco, { url: urlEventHub(), deviceId, sessionId, plataforma: detectarPlataformaPwa() });
    } finally {
      banco.close();
    }
  } catch (error) {
    logger.error.andTrackError(error instanceof Error ? error : new Error(String(error)));
  }
}
